import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MAX_WIDTH = 260;
const MARGIN = 8;

/**
 * Single-line text that ellipsises when cramped and, ONLY when it actually is cut off, shows the
 * full text in a custom tooltip on hover / keyboard focus (ZAR-2371 / SBS-04 — "Online Ord…" in
 * the Sales by Channel / Source table). Text that fits shows no tooltip. The bubble matches
 * KpiTooltip (surface, card border, soft shadow, caret) and is portalled + `position: fixed` so the
 * card's overflow can't clip it; it sits above the text, clamped to the viewport.
 */
export default function TruncatedText({ text, style }: { text: string; style?: React.CSSProperties }) {
    const textRef = useRef<HTMLSpanElement>(null);
    const bubbleRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, caretLeft: 0 });

    const show = () => {
        const el = textRef.current;
        if (el && el.scrollWidth > el.clientWidth) setOpen(true);
    };
    const hide = () => setOpen(false);

    // Place after render so the bubble's real width centres it over the text.
    useLayoutEffect(() => {
        if (!open || !textRef.current || !bubbleRef.current) return;
        const t = textRef.current.getBoundingClientRect();
        const w = bubbleRef.current.getBoundingClientRect().width;
        const centerX = t.left + Math.min(t.width, textRef.current.scrollWidth) / 2;
        const left = Math.max(MARGIN, Math.min(centerX - w / 2, window.innerWidth - w - MARGIN));
        setPos({ top: t.top - 8, left, caretLeft: Math.max(10, Math.min(centerX - left, w - 10)) });
    }, [open]);

    // A scrolling page would leave a fixed bubble behind — close it instead.
    useLayoutEffect(() => {
        if (!open) return undefined;
        window.addEventListener('scroll', hide, { passive: true, capture: true });
        return () => window.removeEventListener('scroll', hide, { capture: true } as any);
    }, [open]);

    return (
        <>
            <span
                ref={textRef}
                tabIndex={-1}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                style={{ display: 'block', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', outline: 'none', ...style }}
            >
                {text}
            </span>
            {open && createPortal(
                <div
                    ref={bubbleRef}
                    role="tooltip"
                    style={{
                        position: 'fixed',
                        top: pos.top,
                        left: pos.left,
                        transform: 'translateY(-100%)',
                        width: 'max-content',
                        maxWidth: MAX_WIDTH,
                        background: 'var(--surface, #fff)',
                        border: '1px solid var(--card-border, #E2E8F0)',
                        borderRadius: 8,
                        padding: '6px 10px',
                        fontSize: 12,
                        fontWeight: 500,
                        color: 'var(--t1, #1A1D2E)',
                        lineHeight: 1.4,
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
                        zIndex: 9999,
                        pointerEvents: 'none',
                    }}
                >
                    {text}
                    <div style={{
                        position: 'absolute',
                        bottom: -5,
                        left: pos.caretLeft,
                        transform: 'translateX(-50%) rotate(45deg)',
                        width: 8,
                        height: 8,
                        background: 'var(--surface, #fff)',
                        borderRight: '1px solid var(--card-border, #E2E8F0)',
                        borderBottom: '1px solid var(--card-border, #E2E8F0)',
                    }} />
                </div>,
                document.body,
            )}
        </>
    );
}
